import { Injectable } from '@angular/core';
import { Query, Apollo } from 'apollo-angular';
import gql from 'graphql-tag';
import { jsonToGraphQLQuery } from 'json-to-graphql-query';
import { makeStateKey, TransferState, StateKey } from '@angular/platform-browser';
import { InMemoryCache, NormalizedCacheObject } from '@apollo/client/cache';
import { ApolloClientOptions, DefaultOptions } from '@apollo/client/core';
import { HttpLink } from 'apollo-angular/http';


@Injectable()
export class BaseGQLServiceConfig {
  typeId?: string;
  clientName: string = '';
  url: string = '';
  model: any;
  rootTypeName?: string;
  wrapperTypeName?: string;
  extraProps?: string;
  disableCache?: boolean;
}

export abstract class BaseGQLService<ResponseType> extends Query<ResponseType> {
  cache: InMemoryCache = new InMemoryCache();

  STATE_KEY: StateKey<any>;

  constructor(
    protected config: BaseGQLServiceConfig,
    protected httpLink: HttpLink,
    override apollo: Apollo,
    protected transferState: TransferState
  ) {
    super(apollo);

    this.STATE_KEY = makeStateKey<any>(`apollo.state.${config.clientName}`);

    this.initApolloClient(config.clientName, config.url, config.disableCache);
  }

  private initApolloClient(clientName: string, url: string, disableCache: boolean = false) {
    let options: ApolloClientOptions<NormalizedCacheObject> = {
      cache: new InMemoryCache(),
      link: this.httpLink.create({
        uri: url,
      }),
    };

    if (disableCache) {
      options = { ...options, defaultOptions: this.buildDefaultOptions() };
    }

    this.cache = new InMemoryCache();

    this.apollo.create(options, clientName);

    this.client = clientName;

    const isBrowser = this.transferState.hasKey<any>(this.STATE_KEY);

    if (isBrowser) {
      this.onBrowser();
    } else {
      this.onServer();
    }
  }

  private buildDefaultOptions(): DefaultOptions {
    return {
      watchQuery: {
        fetchPolicy: 'no-cache',
        errorPolicy: 'ignore',
      },
      query: {
        fetchPolicy: 'no-cache',
        errorPolicy: 'all',
      },
    };
  }

  onServer() {
    this.transferState.onSerialize(this.STATE_KEY, () => {
      return this.cache.extract();
    });
  }

  onBrowser() {
    const state = this.transferState.get<any>(this.STATE_KEY, null);

    this.cache.restore(state);
  }

  public buildGQLProps() {
    const result = jsonToGraphQLQuery(new this.config.model(), { includeFalsyKeys: true });

    return result;
  }

  override document = gql`
      {
        ${this.config.rootTypeName}${this.config.typeId ? `(id: "${this.config.typeId}")` : ''}
        {
            ${this.config.wrapperTypeName
      ? `${this.config.wrapperTypeName} { ${this.buildGQLProps()} } ${this.config.extraProps}`
      : `${this.buildGQLProps()}`
    }
        }
      }`;
}
